'use client';

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAuth } from "@/context/AuthContext";
import { useCorporate } from "@/context/CorporateContext";
import {
    Home,
    Coins,
    ArrowRightLeft,
    Send,
    Network,
    ShieldCheck,
    LogOut,
    ChevronRight,
    Wallet,
    CreditCard,
    ShoppingBag,
    Users,
    Terminal,
    Landmark,
    Settings,
    Calculator,
    Sliders,
    Scale,
    BookOpen,
    Activity,
    Building2,
    Bell,
    Vault,
    X,
    ChevronLeft,
    Layers,
    Sparkles
} from 'lucide-react';

interface OperatorSession {
    id: string;
    username: string;
    role: string;
    is_holding: boolean;
    corporate_id?: string;
    corporate?: {
        id: string;
        name: string;
        slug: string;
    };
}

export interface SidebarProps {
    isOpenMobile?: boolean;
    onCloseMobile?: () => void;
    isCollapsedDesktop?: boolean;
    onToggleCollapseDesktop?: () => void;
}

export default function Sidebar({ 
    isOpenMobile = false, 
    onCloseMobile,
    isCollapsedDesktop = false,
    onToggleCollapseDesktop
}: SidebarProps = {}) {
    const pathname = usePathname();
    const { logout } = useAuth();
    const { config: corporateConfig } = useCorporate();
    const [operatorSession, setOperatorSession] = useState<OperatorSession | null>(null);

    // Auto-cerrar sidebar en mobile al navegar entre rutas
    useEffect(() => {
        if (onCloseMobile) {
            onCloseMobile();
        }
    }, [pathname]);

    // Cargar sesión de operador para determinar si es Holding o Corporate Admin
    useEffect(() => {
        let isMounted = true;
        async function fetchMe() {
            try {
                const res = await fetch('/api/master/me');
                if (res.ok) {
                    const data = await res.json();
                    if (isMounted && data?.authenticated && data?.user) {
                        setOperatorSession(data.user);
                    }
                }
            } catch (err) {
                // Silently fail if not on master session
            }
        }

        if (pathname?.startsWith('/master')) {
            fetchMe();
        }

        return () => {
            isMounted = false;
        };
    }, [pathname]);

    // Ocultar Sidebar en Muros de Autenticación
    if (pathname === '/login' || pathname === '/hub' || pathname === '/master/login' || pathname === '/master/mfa') {
        return null;
    }

    const isMerchantView = pathname?.startsWith('/merchant');
    const isMasterView = pathname?.startsWith('/master');
    const isCorpAdmin = operatorSession?.role === 'corporate_admin';

    const holdingMasterGroups = [
        {
            group: "Torre de Control",
            items: [
                { name: 'Visión Global', path: '/master', icon: Home },
                { name: 'Multi-Tenant Hub', path: '/master/tenants', icon: Landmark },
                { name: 'Pasarelas & 4-Eyes', path: '/master/gateways', icon: CreditCard },
                { name: 'Tesorería & Recaudadoras', path: '/master/treasury', icon: Vault },
                { name: 'Asistentes & Auto-Earn', path: '/master/agents', icon: Sparkles },
                { name: 'Liquidaciones & Clearing', path: '/master/settlements', icon: Layers },
                { name: 'Parámetros Globales', path: '/master/config', icon: Sliders },
                { name: 'Notificaciones E2E', path: '/master/notifications', icon: Bell },
            ]
        },
        {
            group: "Negocio & Activos",
            items: [
                { name: 'Activos & Comisiones', path: '/master/products', icon: Coins },
                { name: 'Créditos & TyC Legal', path: '/master/credit', icon: CreditCard },
                { name: 'Usuarios & Tenants (CRUD)', path: '/master/usuarios', icon: Users },
                { name: 'Consola Token FTK', path: '/master/token', icon: Terminal },
            ]
        },
        {
            group: "RegTech & Contabilidad",
            items: [
                { name: 'Torre RegTech CNV/UIF', path: '/master/compliance', icon: Scale },
                { name: 'Tercerización (Sec. 10)', path: '/master/compliance/third-parties', icon: Building2 },
                { name: 'Fraude & Score BCRA', path: '/master/compliance/fraud', icon: ShieldCheck },
                { name: 'Regímenes BCRA (R.I.)', path: '/master/compliance/reports', icon: BookOpen },
                { name: 'Partida Doble & Solvencia', path: '/master/ledger', icon: Layers },
                { name: 'Observabilidad & DLQ', path: '/master/observability', icon: Activity },
            ]
        },
        {
            group: "Seguridad & Acceso",
            items: [
                { name: 'Operadores & 2FA (MFA)', path: '/master/security', icon: ShieldCheck },
            ]
        }
    ];

    const corporateAdminGroups = [
        {
            group: "Torre de Control",
            items: [
                { name: 'Métricas Corporate', path: '/master', icon: Home },
                { name: 'Mi Corporate', path: '/master/tenants', icon: Landmark },
                { name: 'Pasarelas & 4-Eyes', path: '/master/gateways', icon: CreditCard },
                { name: 'Tesorería de Red', path: '/master/treasury', icon: Vault },
                { name: 'Asistentes & Auto-Earn', path: '/master/agents', icon: Sparkles },
                { name: 'Liquidaciones & Clearing', path: '/master/settlements', icon: Layers },
                { name: 'Notificaciones', path: '/master/notifications', icon: Bell },
            ]
        },
        {
            group: "Negocio & Red Comercial",
            items: [
                { name: 'Reglas de Comisión', path: '/master/products', icon: Coins },
                { name: 'Créditos & TyC Legal', path: '/master/credit', icon: CreditCard },
                { name: 'Usuarios de mi Red', path: '/master/usuarios', icon: Users },
            ]
        },
        {
            group: "RegTech & Contabilidad",
            items: [
                { name: 'Auditoría & RegTech CNV', path: '/master/compliance', icon: Scale },
                { name: 'Tercerización (Sec. 10)', path: '/master/compliance/third-parties', icon: Building2 },
                { name: 'Fraude & Score BCRA', path: '/master/compliance/fraud', icon: ShieldCheck },
                { name: 'Regímenes BCRA (R.I.)', path: '/master/compliance/reports', icon: BookOpen },
                { name: 'Libro Mayor / Solvencia', path: '/master/ledger', icon: Layers },
            ]
        }
    ];

    const merchantGroups = [
        {
            group: "Gestión Comercial",
            items: [
                { name: 'Visión General', path: '/merchant', icon: Home, show: true },
                { name: 'Billeteras & Capital', path: '/merchant/wallets', icon: Wallet, show: true },
                { name: 'Terminal POS', path: '/merchant/pos', icon: CreditCard, show: corporateConfig?.capabilities?.posMerchant },
                { name: 'Pago Proveedores', path: '/merchant/payments', icon: Send, show: true },
            ].filter(i => i.show !== false)
        }
    ];

    const activeGroups = isMerchantView 
        ? merchantGroups 
        : isCorpAdmin 
            ? corporateAdminGroups 
            : holdingMasterGroups;

    // Título y branding superior dinámico
    const brandTitle = isCorpAdmin
        ? (operatorSession?.corporate?.name || 'Mi Corporate')
        : (corporateConfig?.name || 'Corporate Hub');

    const brandNodeLabel = isMerchantView
        ? 'Commerce Node'
        : isCorpAdmin
            ? 'Corporate Admin Node'
            : 'Holding Master Node';

    return (
        <aside
            className={`w-72 sm:w-80 md:w-68 max-w-[85vw] ${
                isMasterView ? 'liquid-glass-sidebar-light text-slate-800' : 'liquid-glass-sidebar text-white'
            } flex flex-col fixed top-0 bottom-0 left-0 h-full z-50 md:z-40 transition-all duration-300 ease-in-out ${
                isOpenMobile 
                    ? 'translate-x-0 shadow-2xl' 
                    : isCollapsedDesktop 
                        ? '-translate-x-full md:-translate-x-full' 
                        : '-translate-x-full md:translate-x-0'
            }`}
        >
            <div className="p-6 md:p-8 pt-6 md:pt-10 relative flex items-start justify-between border-b md:border-b-0 border-slate-200/50">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1.5">
                        <div className="size-11 bg-white rounded-2xl flex items-center justify-center shadow-md p-1.5 overflow-hidden border border-slate-200/90 shrink-0">
                            {corporateConfig?.logoUrl && corporateConfig.logoUrl !== '/favicon.ico' ? (
                                <img src={corporateConfig.logoUrl} alt="Logo" className="w-full h-full object-contain" />
                            ) : (
                                <span className="material-symbols-outlined text-blue-600 font-black">medical_services</span>
                            )}
                        </div>
                        <div className="min-w-0">
                            <h1 className={`text-base sm:text-lg md:text-xl font-black tracking-tighter leading-tight truncate ${isMasterView ? 'text-slate-900' : 'text-white'}`}>
                                {brandTitle}
                            </h1>
                            <p className="text-[var(--tenant-primary)] font-black text-[9px] uppercase tracking-[0.25em] opacity-90 truncate">
                                {brandNodeLabel}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-1 shrink-0 ml-2">
                    {/* Botón contraer en escritorio */}
                    {onToggleCollapseDesktop && (
                        <button
                            onClick={onToggleCollapseDesktop}
                            className={`hidden md:flex p-2 rounded-xl transition cursor-pointer shrink-0 border ${
                                isMasterView
                                    ? 'text-slate-500 hover:text-slate-900 hover:bg-slate-100/80 border-slate-200/60'
                                    : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'
                            }`}
                            aria-label="Ocultar barra lateral"
                            title="Ocultar barra lateral"
                        >
                            <ChevronLeft className="size-4" />
                        </button>
                    )}

                    {/* Botón cerrar para mobile */}
                    <button
                        onClick={onCloseMobile}
                        className={`md:hidden p-2 rounded-xl transition cursor-pointer shrink-0 border ${
                            isMasterView
                                ? 'text-slate-500 hover:text-slate-900 hover:bg-slate-100/80 border-slate-200/60'
                                : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'
                        }`}
                        aria-label="Cerrar menú"
                        title="Cerrar menú"
                    >
                        <X className="size-5" />
                    </button>
                </div>
            </div>

            <nav className="flex-1 px-4 mt-4 md:mt-6 overflow-y-auto pb-6 scrollbar-hide">
                {activeGroups.map((g, idx) => (
                    <div key={idx} className="mb-6">
                        <p className={`px-5 mb-3 text-[9px] font-black uppercase tracking-[0.25em] ${isMasterView ? 'text-slate-400' : 'text-white/40'}`}>
                            {g.group}
                        </p>
                        <div className="space-y-1.5">
                            {g.items.map((item: any) => {
                                const isActive = pathname === item.path;
                                const Icon = item.icon;
                                return (
                                    <Link
                                        key={item.name}
                                        href={item.path}
                                        onClick={() => {
                                            if (onCloseMobile) onCloseMobile();
                                        }}
                                        className={`group flex items-center justify-between px-5 py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all duration-200 relative overflow-hidden ${
                                            isActive
                                                ? isMasterView 
                                                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/25'
                                                    : 'liquid-glass-tint text-white'
                                                : isMasterView
                                                    ? 'text-slate-600 hover:bg-slate-100/80 hover:text-slate-900'
                                                    : 'text-white/45 hover:bg-white/5 hover:text-white/90'
                                            }`}
                                    >
                                        <div className="flex items-center gap-4 relative z-10">
                                            {Icon && <Icon className={`size-4 ${isActive ? 'text-white' : isMasterView ? 'text-slate-400 group-hover:text-blue-600' : 'text-white/30 group-hover:text-[var(--tenant-primary)]'} transition-all duration-300 group-hover:scale-110`} />}
                                            <span>{item.name}</span>
                                        </div>
                                        {isActive && (
                                            <div className={`size-1.5 rounded-full ${isMasterView ? 'bg-white shadow-[0_0_8px_white]' : 'bg-[var(--tenant-primary)] shadow-[0_0_10px_var(--tenant-primary)]'} animate-pulse relative z-10`} />
                                        )}
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </nav>

            <div className="p-6">
                <div className={`p-5 rounded-[2rem] relative overflow-hidden group/card ${isMasterView ? 'liquid-glass-card-light' : 'liquid-glass'}`}>
                    <div className="absolute -right-4 -top-4 size-16 bg-[var(--tenant-primary)]/20 rounded-full blur-2xl group-hover/card:scale-150 transition-transform duration-700"></div>

                    {isMerchantView ? (
                        <div className="flex items-center gap-3 relative z-10">
                            <div className="size-11 rounded-2xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center text-emerald-600 font-black text-xs shadow-sm">CM</div>
                            <div className="flex-1 min-w-0">
                                <p className={`text-xs font-black truncate tracking-tight uppercase ${isMasterView ? 'text-slate-900' : 'text-white'}`}>{corporateConfig?.name || 'Commerce'}</p>
                                <p className={`text-[9px] font-bold uppercase tracking-widest ${isMasterView ? 'text-slate-400' : 'text-white/45'}`}>Active Node</p>
                            </div>
                        </div>
                    ) : isCorpAdmin ? (
                        <div className="flex items-center gap-3 relative z-10">
                            <div className="size-11 rounded-2xl bg-indigo-600 border border-indigo-400/40 flex items-center justify-center text-white font-black text-xs shadow-sm uppercase">CA</div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-black truncate tracking-tight uppercase text-slate-900">
                                    {operatorSession?.username || operatorSession?.corporate?.name || 'Corporate Admin'}
                                </p>
                                <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">
                                    Corporate Admin
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-3 relative z-10">
                            <div className="size-11 rounded-2xl bg-blue-600 border border-blue-400/40 flex items-center justify-center text-white font-black text-xs shadow-sm uppercase">AD</div>
                            <div className="flex-1 min-w-0">
                                <p className={`text-xs font-black truncate tracking-tight uppercase ${isMasterView ? 'text-slate-900' : 'text-white'}`}>Master Admin</p>
                                <p className={`text-[9px] font-bold uppercase tracking-widest ${isMasterView ? 'text-slate-400' : 'text-white/45'}`}>Root Authority</p>
                            </div>
                        </div>
                    )}

                    <button
                        onClick={logout}
                        className={`mt-5 flex items-center w-full px-5 py-3.5 text-[9px] font-black uppercase tracking-widest rounded-xl transition-all group ${
                            isMasterView
                                ? 'text-slate-500 hover:text-rose-600 hover:bg-rose-50 border border-slate-200/60 hover:border-rose-200'
                                : 'text-white/45 hover:text-rose-300 hover:bg-rose-500/15 border border-transparent hover:border-rose-400/30'
                        }`}
                    >
                        <LogOut className={`size-4 mr-3 transition-colors ${isMasterView ? 'text-slate-400 group-hover:text-rose-600' : 'text-white/30 group-hover:text-rose-300'}`} />
                        Cerrar Sesión
                    </button>
                </div>
            </div>
        </aside>
    );
}
